<div class="fixed inset-0 bg-gray-50 overflow-y-auto">
<div class="min-h-full flex flex-col items-center justify-center px-4 py-12">
  <div class="w-full max-w-lg">

    <!-- Extraction Progress Badge (top-left, persists across steps) -->
    <div class="fixed top-4 left-4 z-50" *ngIf="(extractionStatus$ | async) as extractionStatus">
      <div class="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium shadow-sm"
        [ngClass]="{
          'bg-blue-50 text-blue-700 border border-blue-200': extractionStatus === 'pending' || extractionStatus === 'processing',
          'bg-green-50 text-green-700 border border-green-200': extractionStatus === 'completed',
          'bg-red-50 text-red-700 border border-red-200': extractionStatus === 'failed'
        }">
        <!-- Animated dot for processing -->
        <span class="w-2 h-2 rounded-full animate-pulse bg-blue-500"
          *ngIf="extractionStatus === 'pending' || extractionStatus === 'processing'"></span>
        <!-- Success check -->
        <svg *ngIf="extractionStatus === 'completed'" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
        <!-- Error icon -->
        <svg *ngIf="extractionStatus === 'failed'" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
        <span>{{ getExtractionBadgeText(extractionStatus) }}</span>
      </div>
    </div>

    <!-- Back Arrow and Step Title -->
    <div class="flex items-center justify-between mb-8">
      <button
        type="button"
        (click)="previousStep()"
        class="text-gray-500 hover:text-gray-700 transition-colors"
        [class.invisible]="step === 1">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>

      <div class="text-sm text-gray-500 font-medium">{{ getStepTitle() }}</div>

      <!-- Progress Dots -->
      <div class="flex gap-1.5">
        <span
          *ngFor="let active of getProgressDots()"
          class="w-2 h-2 rounded-full transition-colors"
          [ngClass]="active ? 'bg-indigo-600' : 'bg-gray-300'">
        </span>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!-- STEP 1: Website URL + Company Name -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    <div *ngIf="step === 1">
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Let's get started</h2>
        <p class="text-gray-500 mb-8">
          Enter your company website and we'll automatically analyze your products, services, and target audience.
        </p>

        <form [formGroup]="form">
          <div class="space-y-5">
            <app-input-field
              label="Website URL"
              placeholder="https://yourcompany.com"
              controlName="website_url"
              [required]="true"
              type="url"
              patternErrorMessage="Please enter a valid URL"
            ></app-input-field>

            <app-input-field
              label="Company Name"
              placeholder="Your Company (optional)"
              controlName="company_name"
              [required]="false"
            ></app-input-field>
          </div>
        </form>

        <div class="mt-6 flex items-start gap-2 text-sm text-gray-400" *ngIf="!(extractionStatus$ | async)">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
          <span>We'll analyze your website in the background. You can continue while it works.</span>
        </div>
      </div>

      <!-- Continue Button -->
      <div class="mt-6">
        <button
          type="button"
          class="w-full py-3 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          [disabled]="isContinueDisabled() || (sessionLoading$ | async)"
          (click)="nextStep()">
          <span *ngIf="!(sessionLoading$ | async)">Continue</span>
          <span *ngIf="(sessionLoading$ | async)">Loading...</span>
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!-- STEP 2: Industries, Job Titles, Countries -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    <div *ngIf="step === 2">
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">Who are you targeting?</h2>
        <p class="text-gray-500 mb-8">
          Tell us about your ideal customers so we can find the best leads for you.
        </p>

        <form [formGroup]="form">
          <div class="space-y-6">

            <!-- Target Industries -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Target Industries <span class="text-red-500">*</span></label>
              <app-combobox-multiselect
                label=""
                placeholder="Select target industries"
                controlName="target_industries"
                [options]="industries"
                displayKey="name"
                valueKey="id"
                (selectionChange)="handleIndustrySelectionChange($event)"
              ></app-combobox-multiselect>
            </div>

            <!-- Target Job Titles -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Target Job Titles <span class="text-red-500">*</span></label>
              <div class="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  *ngFor="let title of targetJobTitleOptions"
                  class="px-3 py-1.5 rounded-full text-sm border transition-colors"
                  [ngClass]="isJobTitleSelected(title.id)
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'"
                  (click)="toggleJobTitle(title.id)">
                  {{ title.name }}
                </button>
              </div>

              <div *ngIf="isOtherJobTitleSelected" class="flex gap-2 mb-1">
                <input type="text"
                       class="flex-1 rounded-lg border-gray-300 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                       [attr.maxlength]="maxCustomTargetLength"
                       placeholder="Type a custom job title and press Enter"
                       [(ngModel)]="customJobTitleInput"
                       [ngModelOptions]="{standalone: true}"
                       (keydown.enter)="$event.preventDefault(); addCustomJobTitle()">
                <button type="button"
                        class="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                        (click)="addCustomJobTitle()">
                  Add
                </button>
              </div>

              <!-- Selected Tags -->
              <div class="flex flex-wrap gap-2 mt-3" *ngIf="selectedJobTitles.size > 0">
                <span
                  *ngFor="let title of selectedJobTitles"
                  class="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-sm rounded-full">
                  {{ title }}
                  <button type="button" class="text-indigo-400 hover:text-indigo-600" (click)="removeJobTitle(title)">&times;</button>
                </span>
              </div>
            </div>

            <!-- Target Countries -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Target Countries <span class="text-red-500">*</span></label>
              <app-combobox-multiselect
                label=""
                placeholder="Select target countries"
                controlName="target_countries"
                [options]="countries"
                displayKey="name"
                valueKey="name"
                (selectionChange)="handleCountrySelectionChange($event)"
              ></app-combobox-multiselect>
            </div>

          </div>
        </form>

        <!-- Extraction Results Preview -->
        <div class="mt-8 border-t border-gray-100 pt-6" *ngIf="(isExtractionComplete$ | async)">
          <h3 class="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Website Analysis Results</h3>

          <div class="space-y-4">
            <div *ngIf="(productDescription$ | async) as description">
              <h4 class="text-sm font-medium text-gray-700 mb-1">Product/Service</h4>
              <p class="text-sm text-gray-600">{{ description }}</p>
            </div>

            <div class="grid grid-cols-2 gap-4" *ngIf="(extractionBenefits$ | async) as benefits">
              <div>
                <h4 class="text-sm font-medium text-gray-700 mb-2">Key Benefits</h4>
                <ul class="space-y-1">
                  <li *ngFor="let benefit of benefits" class="text-xs text-gray-600 flex items-start gap-1.5">
                    <span class="text-green-500 mt-0.5">&#8226;</span>
                    {{ benefit }}
                  </li>
                </ul>
              </div>

              <div *ngIf="(extractionPains$ | async) as pains">
                <h4 class="text-sm font-medium text-gray-700 mb-2">Customer Pains</h4>
                <ul class="space-y-1">
                  <li *ngFor="let pain of pains" class="text-xs text-gray-600 flex items-start gap-1.5">
                    <span class="text-red-400 mt-0.5">&#8226;</span>
                    {{ pain }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="mt-6 flex gap-3">
        <button
          type="button"
          class="flex-1 py-3 px-6 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg border border-gray-300 transition-colors"
          (click)="previousStep()">
          Back
        </button>
        <button
          type="button"
          class="flex-[2] py-3 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          [disabled]="(completeLoading$ | async)"
          (click)="completeOnboarding()">
          <span *ngIf="!(completeLoading$ | async)">Complete Setup</span>
          <span *ngIf="(completeLoading$ | async)">Completing...</span>
        </button>
      </div>
    </div>

  </div>
</div>
</div>
